/**
 * ImageCropModal — modal de recorte especializado para Avatar (usuário) e
 * Logo (negócio). Diferente do `ImageCropper` existente (Instagram presets),
 * este componente respeita as regras específicas de cada caso:
 *
 * mode="avatar":
 *   - Máscara circular (visual). Recorte quadrado 1:1.
 *   - Modo "cover": zoom + arraste, corta sobras.
 *   - Saída: WebP, qualidade 82, máx 512×512.
 *
 * mode="logo":
 *   - Máscara quadrada com cantos levemente arredondados.
 *   - Modo "contain": a imagem inteira cabe dentro do quadro, com
 *     padding automático. Nunca corta bordas do logo.
 *   - Se PNG com alfa: mantém transparência. Se JPG: fundo branco.
 *   - Saída: PNG (se original PNG com transparência) ou WebP (senão),
 *     máx 512×512.
 *
 * Atalho "Usar recorte automático": pula o ajuste manual (viés leve pra
 * cima em avatar; fit centralizado em logo).
 *
 * Props:
 *   open: boolean
 *   file: File (imagem escolhida pelo usuário)
 *   mode: 'avatar' | 'logo'
 *   onConfirm({ blob, cropData })
 *   onCancel()
 *
 * cropData retornado (persistir junto ao doc):
 *   { zoom, x, y, mode } — usado para reabrir o editor no futuro com o
 *   mesmo enquadramento pré-carregado.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Check, ZoomIn, Wand2, Loader2, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';

const OUTPUT_SIZE = 512;
const MIN_DIMENSION = 200;   // rejeitado antes do crop se abaixo disso
const WEBP_QUALITY = 0.82;

async function fileHasAlpha(file) {
  if (!file || !file.type || !file.type.includes('png')) return false;
  // Detect alfa por chunk tRNS ou canal α em pixel. Aproximação leve:
  // um PNG com tRNS quase sempre tem alpha real.
  const buf = await file.slice(0, 4096).arrayBuffer();
  const arr = new Uint8Array(buf);
  const text = new TextDecoder('latin1').decode(arr);
  return text.includes('tRNS') || text.includes('IDAT'); // conservador
}

function drawAvatar(ctx, img, cropRect) {
  // cropRect = { sx, sy, sSize } no espaço da imagem original
  ctx.save();
  ctx.clearRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  ctx.drawImage(
    img,
    cropRect.sx, cropRect.sy, cropRect.sSize, cropRect.sSize,
    0, 0, OUTPUT_SIZE, OUTPUT_SIZE,
  );
  ctx.restore();
}

function drawLogoContain(ctx, img, preserveAlpha) {
  // contain: escala a imagem inteira dentro de OUTPUT_SIZE×OUTPUT_SIZE
  // e preenche o restante com fundo branco (ou transparente se PNG alfa).
  ctx.clearRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  if (!preserveAlpha) {
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  }
  const scale = Math.min(OUTPUT_SIZE / img.naturalWidth, OUTPUT_SIZE / img.naturalHeight);
  const w = img.naturalWidth * scale;
  const h = img.naturalHeight * scale;
  const dx = (OUTPUT_SIZE - w) / 2;
  const dy = (OUTPUT_SIZE - h) / 2;
  ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, dx, dy, w, h);
}

export default function ImageCropModal({ open, file, mode = 'avatar', onConfirm, onCancel }) {
  const [imgSrc, setImgSrc] = useState('');
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);            // avatar only
  const [offset, setOffset] = useState({ x: 0.5, y: 0.5 }); // avatar only, fração
  const [preserveAlpha, setPreserveAlpha] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const imgRef = useRef(null);
  const stageRef = useRef(null);
  const dragStart = useRef(null);

  useEffect(() => {
    if (!file) { setImgSrc(''); return; }
    const reader = new FileReader();
    reader.onload = (e) => setImgSrc(e.target.result);
    reader.readAsDataURL(file);

    (async () => {
      const has = await fileHasAlpha(file);
      setPreserveAlpha(has);
    })();
  }, [file]);

  useEffect(() => {
    if (open) {
      setZoom(1);
      setOffset({ x: 0.5, y: mode === 'avatar' ? 0.45 : 0.5 }); // avatar: leve viés pra cima
    }
  }, [open, mode]);

  const onImgLoad = useCallback((e) => {
    const w = e.target.naturalWidth;
    const h = e.target.naturalHeight;
    setImgSize({ w, h });
    if (w < MIN_DIMENSION || h < MIN_DIMENSION) {
      toast.error(`Imagem muito pequena (${w}×${h}). Mínimo aceito: ${MIN_DIMENSION}×${MIN_DIMENSION}px.`);
      onCancel?.();
    }
  }, [onCancel]);

  // Compute avatar crop rect in original-image space based on zoom + offset.
  const getAvatarCropRect = useCallback(() => {
    const { w, h } = imgSize;
    if (!w || !h) return null;
    const shortSide = Math.min(w, h);
    const sSize = shortSide / zoom;         // tamanho do quadrado no espaço original
    const maxX = w - sSize;
    const maxY = h - sSize;
    const sx = Math.max(0, Math.min(maxX, offset.x * w - sSize / 2));
    const sy = Math.max(0, Math.min(maxY, offset.y * h - sSize / 2));
    return { sx, sy, sSize };
  }, [imgSize, zoom, offset]);

  // Drag para reposicionar (só avatar)
  const onMouseDown = (e) => {
    if (mode !== 'avatar') return;
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onMouseMove = (e) => {
    if (!dragging || !stageRef.current) return;
    const rect = stageRef.current.getBoundingClientRect();
    const dx = (e.clientX - dragStart.current.x) / rect.width;
    const dy = (e.clientY - dragStart.current.y) / rect.height;
    setOffset({
      x: Math.max(0, Math.min(1, dragStart.current.ox - dx)),
      y: Math.max(0, Math.min(1, dragStart.current.oy - dy)),
    });
  };
  const onMouseUp = () => setDragging(false);

  const autoCrop = () => {
    setZoom(1);
    setOffset({ x: 0.5, y: mode === 'avatar' ? 0.45 : 0.5 });
    toast.success('Recorte automático aplicado.');
  };

  const confirm = useCallback(async () => {
    if (!imgRef.current || !imgSize.w) return;
    setBusy(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext('2d');

      let cropData;
      if (mode === 'avatar') {
        const rect = getAvatarCropRect();
        if (!rect) throw new Error('Recorte inválido');
        drawAvatar(ctx, imgRef.current, rect);
        cropData = { zoom, x: offset.x, y: offset.y, mode: 'cover' };
      } else {
        drawLogoContain(ctx, imgRef.current, preserveAlpha);
        cropData = { zoom: 1, x: 0.5, y: 0.5, mode: 'contain' };
      }

      // Escolhe formato de saída
      const outputType = (mode === 'logo' && preserveAlpha) ? 'image/png' : 'image/webp';
      const quality = outputType === 'image/webp' ? WEBP_QUALITY : undefined;
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Falha ao gerar imagem.'))), outputType, quality);
      });
      onConfirm?.({ blob, cropData, outputType });
    } catch (err) {
      toast.error(err.message || 'Erro ao processar imagem.');
    } finally {
      setBusy(false);
    }
  }, [mode, imgSize, offset, zoom, preserveAlpha, getAvatarCropRect, onConfirm]);

  if (!open) return null;

  const isAvatar = mode === 'avatar';

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4"
      role="dialog" aria-modal="true"
      data-testid="image-crop-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[95vh]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 flex-shrink-0">
          <div>
            <h2 className="font-bold text-lg" style={{ fontFamily: 'Manrope' }}>
              {isAvatar ? 'Ajustar foto de perfil' : 'Ajustar logo do negócio'}
            </h2>
            <p className="text-xs text-gray-500">
              {isAvatar
                ? 'A foto será exibida em círculo em todo o app.'
                : 'A logo será exibida completa dentro de um quadrado (sem cortar).'}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-2 rounded-full hover:bg-gray-100"
            aria-label="Fechar"
            data-testid="crop-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stage */}
        <div className="flex-1 overflow-hidden p-4 flex flex-col items-center justify-center bg-gray-50">
          <div
            ref={stageRef}
            className="relative w-72 h-72 sm:w-80 sm:h-80 overflow-hidden select-none"
            style={{ cursor: isAvatar ? (dragging ? 'grabbing' : 'grab') : 'default' }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          >
            {/* Placeholder para o "canvas" visual — quadrado com cantos. */}
            <div
              className={
                isAvatar
                  ? 'absolute inset-0 rounded-full bg-white/0 ring-2 ring-white/70 pointer-events-none z-10'
                  : 'absolute inset-0 rounded-xl bg-white/0 ring-2 ring-white/70 pointer-events-none z-10'
              }
              style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)' }}
            />
            {imgSrc && isAvatar && (
              <img
                ref={imgRef}
                src={imgSrc}
                alt="Ajustar"
                onLoad={onImgLoad}
                className="absolute pointer-events-none"
                draggable={false}
                style={(() => {
                  // Mostra a imagem já com o crop atual aplicado (preview WYSIWYG).
                  const rect = getAvatarCropRect();
                  if (!rect || !imgSize.w) return { visibility: 'hidden' };
                  // Escala para o stage 288px (w-72). Ajuste responsivo simples.
                  const stageSize = 288;
                  const s = stageSize / rect.sSize;
                  return {
                    left: -rect.sx * s,
                    top: -rect.sy * s,
                    width: imgSize.w * s,
                    height: imgSize.h * s,
                    maxWidth: 'none',
                  };
                })()}
              />
            )}
            {imgSrc && !isAvatar && (
              <div className={`absolute inset-0 flex items-center justify-center ${preserveAlpha ? '' : 'bg-white'} rounded-xl`}>
                <img
                  ref={imgRef}
                  src={imgSrc}
                  alt="Ajustar"
                  onLoad={onImgLoad}
                  className="max-w-full max-h-full object-contain pointer-events-none"
                  draggable={false}
                />
              </div>
            )}
          </div>

          {isAvatar && (
            <div className="w-full max-w-xs mt-4 flex items-center gap-2 text-sm">
              <ZoomIn className="w-4 h-4 text-gray-500" />
              <input
                type="range"
                min="1"
                max="3"
                step="0.05"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="flex-1 accent-purple-600"
                data-testid="crop-zoom-slider"
                aria-label="Zoom"
              />
              <span className="text-xs text-gray-500 w-10 text-right">{zoom.toFixed(2)}×</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between gap-2 flex-shrink-0 bg-gray-50">
          <button
            onClick={autoCrop}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50 rounded-lg"
            data-testid="crop-auto-btn"
          >
            <Wand2 className="w-4 h-4" />
            Usar recorte automático
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100"
              data-testid="crop-cancel-btn"
            >
              Cancelar
            </button>
            <button
              onClick={confirm}
              disabled={busy || !imgSize.w}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60"
              data-testid="crop-confirm-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Exports auxiliares (facilitam testes)
export { OUTPUT_SIZE as CROP_OUTPUT_SIZE, MIN_DIMENSION as CROP_MIN_DIMENSION };
